import React, { FC, useEffect, useState, SyntheticEvent } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Popper, SxProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import StringUtils from '../../utils/stringUtils';

export interface TwTagsOption {
  id: string;
  subId?: string;
  label: string;
  subLabel?: string;
  value: unknown;
  groupBy?: string;
}
export interface TwTagsOptionProps {
  props: React.HTMLAttributes<HTMLLIElement>;
  option: any;
}

export const RenderTagOption: React.FC<TwTagsOptionProps> = ({ props, option }) => {
  return (
    <Box component="span" {...props} sx={{ padding: 'inherit' }} key={props.id}>
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-start"
        alignItems="flex-start"
        sx={{ width: '100%' }}
      >
        <Stack
          spacing={0}
          direction="column"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ width: '100%' }}
        >
          <Typography variant="subtitle2" fontWeight="400" color="#1F2937" fontSize="14px">
            {option.label}
          </Typography>

          <Typography variant="subtitle2" fontWeight="400" color="#9CA3AF" fontSize={'14px'}>
            {option.subLabel}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export type TwTagsFieldType = 'email' | 'phone';
export interface TwTagsFieldProps {
  id: string;
  name?: string;
  label?: string;
  suggestedOptions?: TwTagsOption[];
  tagsDisplayed?: string[];
  onTagAdded?: (tags: string[]) => void;
  onChange?: (event: React.SyntheticEvent<Element, Event>, value: string[]) => void;
  tagType?: TwTagsFieldType;
  placeholder?: string;
  sx?: SxProps;
  hasError?: boolean;
  disabled?: boolean;
}

const TagsAutocomplete = styled(Autocomplete)(() => ({
  '& .MuiAutocomplete-inputRoot': {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '14px',
    lineHeight: '21px',
    backgroundColor: 'white',
    minHeight: '40px',
    marginBottom: '8px',
    padding: '6px',
    borderColor: '#E2E2EA'
  },

  '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
    padding: '0 4px 0 6px'
  },

  '& .MuiAutocomplete-tag': {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    background: '#F3F4F6',
    color: '#1F2937',
    borderRadius: '4px',
    height: '24px'
  },

  '& .MuiChip-deleteIcon': {
    color: '#9CA3AF',
    width: '16px',
    height: '16px'
  },

  '& .Mui-focused': {
    '& fieldset.MuiOutlinedInput-notchedOutline': {
      borderColor: '#3959df'
    }
  }
}));

const TagsLabel = styled('label')(() => ({
  display: 'flex',
  alignItems: 'center',
  color: '#7e858e',
  fontSize: '14px',
  fontWeight: 500,
  padding: '4px 0px'
}));

const TwTagsField: FC<TwTagsFieldProps> = ({
  id,
  name,
  label,
  tagsDisplayed,
  suggestedOptions,
  onTagAdded,
  onChange,
  tagType,
  placeholder,
  sx,
  hasError,
  disabled
}) => {
  const [value, setValue] = useState<string[]>([]);
  const [sanitizedValues, setSanitizedValues] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const uniqueTagsDisplayed =
      tagsDisplayed &&
      tagsDisplayed.reduce((a: string[], b) => {
        if (a.indexOf(b) < 0) a.push(b);
        return a;
      }, []);

    if (uniqueTagsDisplayed && uniqueTagsDisplayed.length > 0) {
      setValue(uniqueTagsDisplayed);
    }
  }, [tagsDisplayed]);

  useEffect(() => {
    // If we have suggested options to select from, the selected option is added as an object to the autocomplete value array.
    // Here we clean that up and save only the string value we need for the tag.
    if (suggestedOptions) {
      const cleanValues: string[] = [];

      value.forEach((item) => {
        if (typeof item !== 'string') {
          cleanValues.push((item as TwTagsOption).value as string);
        } else {
          if (tagType && tagType === 'email') {
            if (StringUtils.isValidEmail(item)) cleanValues.push(item);
          } else {
            return;
          }
        }
      });
      setSanitizedValues(cleanValues as string[]);
    } else {
      if (tagType && tagType === 'email') {
        const cleanValues = value.filter((tag) => {
          return StringUtils.isValidEmail(tag);
        });
        setSanitizedValues(cleanValues);
      } else {
        setSanitizedValues(value);
      }
    }
  }, [value, suggestedOptions]);

  useEffect(() => {
    onTagAdded &&
      JSON.stringify(sanitizedValues) != JSON.stringify(tagsDisplayed) &&
      onTagAdded(sanitizedValues);
  }, [sanitizedValues]);

  const handleInputBlur = (e: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const currentInputValue = e.currentTarget.value.trim();
    if (!currentInputValue || value.indexOf(currentInputValue) !== -1) {
      return;
    }

    if (tagType && tagType === 'email') {
      const validValue = StringUtils.isValidEmail(currentInputValue);
      validValue && setValue([...value, currentInputValue]);
      setInputValue('');
    } else {
      setValue([...value, currentInputValue]);
      setInputValue('');
    }
  };

  return (
    <Box sx={sx}>
      {label && (
        <TagsLabel className="tid-tags-field__label" htmlFor={id}>
          <span>{label}</span>
        </TagsLabel>
      )}
      <TagsAutocomplete
        multiple
        id={id}
        freeSolo
        disableClearable
        filterOptions={(options: unknown[]): unknown[] =>
          // Exclude suggestions already selected
          options.filter((o) => !sanitizedValues.includes((o as TwTagsOption).value as string))
        }
        filterSelectedOptions
        clearOnBlur
        options={suggestedOptions || []}
        onChange={(e, newValue): void => {
          setValue(newValue as string[]);
          onChange && onChange(e, newValue as string[]);
        }}
        value={sanitizedValues.length > 0 ? sanitizedValues : tagsDisplayed}
        ChipProps={{ deleteIcon: disabled ? <></> : <ClearIcon /> }}
        disabled={disabled ?? false}
        renderInput={(params): JSX.Element => (
          <TextField
            {...params}
            name={name}
            value={inputValue}
            onBlur={handleInputBlur}
            variant="outlined"
            placeholder={tagsDisplayed && tagsDisplayed.length > 0 ? '' : placeholder}
            error={hasError}
            fullWidth
          />
        )}
        renderOption={(props, option): React.ReactNode => {
          return <RenderTagOption props={props} option={option} />;
        }}
        PopperComponent={(props): JSX.Element => (
          <Popper {...props} sx={{ zIndex: '1400 !important' }} />
        )}
      />
    </Box>
  );
};

export default TwTagsField;
